import React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

/*
The offers on ONE checkout block - a seller x order-type, since Cool chain ships as its own order and
the rest together - opened from that block's "Applicable Vouchers" button. Three kinds of offer are
shown, each with an Applicable / Not Applicable status:

  - Medimny Offer: the PLATFORM-tier cashback / store-wide gift (Medimny-funded), applicable once the
    block's order value clears the platform campaign's minimum order value.
  - Seller value tier: the SELLER-tier cashback / store-wide gift (seller-funded), same idea.
  - Product coupons / gifts: the seller's per-item vouchers - always applicable (they carry no minimum).

Claim All is live as long as ONE campaign on this block is still there to be claimed - product,
seller or platform level, whichever it happens to be - and goes dead once they all read Claimed. A
campaign that is Not Applicable (only the value tiers can lock; product rows never do) no longer
holds the button back: it stays listed with its shortfall for a buyer who wants to top the cart up,
while the ones that ARE applicable get claimed now. Pressing Claim All marks this block's applicable
rows "Claimed" - a UI affordance only; the order applies every eligible reward at placement whether
or not the buyer ever opened this modal.

Laid out like the cart's other dialogs: a plain DialogTitle, `h4` rows in full-width tables inside a
DialogContentText, and MUI text Buttons right-aligned in a `pr-4` DialogActions.
*/

const inr = (v) =>
  Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// The row label for a value-tier offer, mirroring the cart's offerBanner / platformReward wording so
// the modal reads the same as the banners. A gift names what it is and what it is worth; a cashback
// names its rupee figure - what it is worth now if unlocked, or what it would be worth at the minimum.
const offerRowText = (offer) => {
  const who = offer.label === 'platform' ? 'Medimny' : 'Seller';
  const from = offer.label === 'platform' ? 'from Medimny' : 'from this seller';
  if (offer.isGift) {
    const name = (offer.gift && offer.gift.description) || 'a free gift';
    const brand = offer.gift && offer.gift.brand ? ` by ${offer.gift.brand}` : '';
    const worth = offer.gift && Number(offer.gift.price) > 0 ? ` worth ₹${inr(offer.gift.price)}` : '';
    return `${name}${brand}${worth}, free ${from}`;
  }
  const amount = offer.unlocked ? offer.cashback : offer.cashbackAtMov;
  return `₹${inr(amount)} ${who} cashback`;
};

// A right-aligned status cell: green once claimed, blue when applicable, grey when not.
const StatusCell = ({ label, tone }) => (
  <td
    className={`h4 ${tone === 'claimed' ? 'text-success' : ''}`}
    style={{
      padding: 0,
      textAlign: 'right',
      whiteSpace: 'nowrap',
      fontWeight: 600,
      color: tone === 'claimed' ? undefined : tone === 'ok' ? '#072791' : '#9aa0b5',
    }}
  >
    {label}
  </td>
);

// A value-tier (Medimny / seller) offer row: its description and its status. A locked offer is Not
// Applicable; an unlocked one reads Applicable, then Claimed once this block's Claim All is pressed
// (the value tiers are tracked per block, so `claimed` is the block's claimed flag).
const OfferRow = ({ offer, claimed }) => {
  const isClaimed = offer.unlocked && claimed;
  const label = !offer.unlocked ? 'Not Applicable' : isClaimed ? 'Claimed' : 'Applicable';
  const tone = !offer.unlocked ? 'no' : isClaimed ? 'claimed' : 'ok';
  return (
    <tr height='25px'>
      <td className='h4' style={{ padding: 0, paddingRight: '10px' }}>{offerRowText(offer)}</td>
      <StatusCell label={label} tone={tone} />
    </tr>
  );
};

const emptyRow = (text) => (
  <tr height='25px'>
    <td className='h4' colSpan={2} style={{ padding: 0, color: '#9aa0b5' }}>{text}</td>
  </tr>
);

const ClaimVouchersModal = (props) => {
  const group = props.group || null;
  const claimedKeys = props.claimedKeys || [];
  // Whether THIS block has been claimed - drives the value-tier rows' "Claimed" state, since those
  // tiers are tracked per block (claimedBlocks) rather than per row (claimedKeys).
  const blockClaimed = Boolean(props.blockClaimed);
  // True once the buyer has pressed "Claim All"; it disables the button - the single press is the
  // whole interaction. Reset every time the modal is opened, so a cart edited since starts clean.
  const [attempted, setAttempted] = React.useState(false);
  React.useEffect(() => { if (props.open) setAttempted(false); }, [props.open]);

  const productRows = group ? group.productRows : [];
  const platformOffer = group ? group.platformOffer : null;
  const sellerLevelOffer = group ? group.sellerLevelOffer : null;

  // What a press would actually win, counted at every level the modal shows:
  //   - a product coupon/gift not yet in claimedKeys (product rows carry no minimum, so they are
  //     applicable whenever they are listed), and
  //   - an UNLOCKED Medimny/seller value tier on a block that has not been claimed yet.
  // A LOCKED value tier counts for nothing here: it cannot be claimed, so it neither enables the
  // button nor keeps it alive once everything claimable is claimed. It stays on the list reading Not
  // Applicable for a buyer who wants to top the cart up to its minimum.
  const valueOffers = [platformOffer, sellerLevelOffer].filter(Boolean);
  const unclaimedApplicable = productRows.filter(
    (row) => row.applied && claimedKeys.indexOf(row.key) === -1,
  );
  const unclaimedValueOffers = blockClaimed
    ? []
    : valueOffers.filter((o) => o.unlocked);
  // Disabled once pressed, and whenever there is nothing left to claim - every applicable campaign,
  // at whatever level, already Claimed. One claimable campaign anywhere on the block keeps it live.
  const nothingLeft =
    unclaimedApplicable.length === 0 && unclaimedValueOffers.length === 0;
  const claimDisabled = attempted || nothingLeft;

  const handleClaimAll = (event) => {
    setAttempted(true);
    // The event goes with it so the parent can burst confetti out of this button. Read synchronously.
    if (props.onClaimAll) props.onClaimAll(event);
  };

  return (
    <Dialog open={Boolean(props.open)} onClose={props.onClose} fullWidth={true} maxWidth='sm'>
      <DialogTitle style={{ paddingBottom: '0px' }}>
        Applicable Vouchers{group ? ` - ${group.blockLabel}` : ''}
      </DialogTitle>
      <DialogContent className='pt-0'>
        {/* `component='div'`: this text block holds the sections and their tables, and
            DialogContentText is a <p> by default - which cannot legally contain them. */}
        <DialogContentText component='div' style={{ marginBottom: '0px' }}>
          {!group &&
            <h4 className='mt-3 mb-0'>There are no offers to claim on this order right now.</h4>
          }

          {group &&
            <React.Fragment>
              {/* 1. Medimny Offer - the platform (Medimny-funded) value tier. */}
              <div className='mt-3'>
                <h4 className='font-weight-bold mb-1'>Medimny Offer</h4>
                <table style={{ width: '100%' }}>
                  <tbody>
                    {platformOffer
                      ? <OfferRow offer={platformOffer} claimed={blockClaimed} />
                      : emptyRow('No Medimny offer on this order.')}
                  </tbody>
                </table>
              </div>

              {/* 2. The seller's own offers: their store-wide value tier, then per-item vouchers. */}
              <div className='mt-3'>
                <h4 className='font-weight-bold mb-1'>{group.sellerName}</h4>
                <table style={{ width: '100%' }}>
                  <tbody>
                    {sellerLevelOffer && <OfferRow offer={sellerLevelOffer} claimed={blockClaimed} />}
                    {productRows.map((row) => {
                      const unlocked = Boolean(row.applied);
                      const isClaimed = unlocked && claimedKeys.indexOf(row.key) > -1;
                      const statusLabel = isClaimed ? 'Claimed' : unlocked ? 'Applicable' : 'Not Applicable';
                      const tone = isClaimed ? 'claimed' : unlocked ? 'ok' : 'no';
                      return (
                        <tr key={row.key} height='25px'>
                          <td className='h4' style={{ padding: 0, paddingRight: '10px' }}>{row.label}</td>
                          <StatusCell label={statusLabel} tone={tone} />
                        </tr>
                      );
                    })}
                    {!sellerLevelOffer && productRows.length === 0 &&
                      emptyRow('No seller offer on this order.')}
                  </tbody>
                </table>
              </div>
            </React.Fragment>
          }
        </DialogContentText>
      </DialogContent>
      <DialogActions className='pr-4'>
        <Button onClick={props.onClose} color='secondary'>Close</Button>
        {group &&
          <Button
            color='primary'
            disabled={claimDisabled}
            onClick={handleClaimAll}
          >
            Claim All
          </Button>
        }
      </DialogActions>
    </Dialog>
  );
};

export default ClaimVouchersModal;
